import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import { 
  Award, Search, Bookmark, BookmarkCheck, DollarSign, Calendar,
  ExternalLink, Loader2, Filter, TrendingUp, CheckCircle
} from 'lucide-react';
import api from '../utils/api';

const Scholarships = () => {
  const [scholarships, setScholarships] = useState([]);
  const [savedScholarships, setSavedScholarships] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);

  const [searchCriteria, setSearchCriteria] = useState({
    gpa: '',
    major: '',
    financial_need: false,
    first_gen: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [allRes, savedRes, categoriesRes, statsRes] = await Promise.all([
        api.get('/scholarships/all'),
        api.get('/scholarships/saved'),
        api.get('/scholarships/categories'),
        api.get('/scholarships/stats')
      ]);

      setScholarships(allRes.data.scholarships || []);
      setSavedScholarships(savedRes.data.saved_scholarships || []);
      setCategories(categoriesRes.data.categories || []);
      setStats(statsRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load scholarships', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const res = await api.post('/scholarships/search', {
        gpa: searchCriteria.gpa ? parseFloat(searchCriteria.gpa) : null,
        major: searchCriteria.major || null,
        financial_need: searchCriteria.financial_need,
        first_gen: searchCriteria.first_gen
      });
      setScholarships(res.data.scholarships || []);
      toast({ title: 'Search Complete', description: `Found ${res.data.total} matching scholarships` });
    } catch (error) {
      toast({ title: 'Error', description: 'Search failed', variant: 'destructive' });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSave = async (scholarshipId, status = 'interested') => {
    try {
      await api.post('/scholarships/save', { scholarship_id: scholarshipId, status });
      toast({ title: 'Success', description: 'Scholarship saved' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save', variant: 'destructive' });
    }
  };

  const handleRemove = async (scholarshipId) => {
    try {
      await api.delete(`/scholarships/saved/${scholarshipId}`);
      toast({ title: 'Success', description: 'Scholarship removed' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to remove', variant: 'destructive' });
    }
  };

  const formatAmount = (amount) => {
    if (typeof amount === 'string') return amount;
    return `$${amount.toLocaleString()}`;
  };

  const isSaved = (scholarshipId) => savedScholarships.some(s => s.id === scholarshipId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Scholarship Matching</h1>
          <p className="text-gray-600">Find scholarships you qualify for and track your applications</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">{stats.total_saved}</p>
                  <p className="text-sm text-blue-800">Saved</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-amber-600">{stats.applied}</p>
                  <p className="text-sm text-amber-800">Applied</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">{stats.received}</p>
                  <p className="text-sm text-green-800">Received</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-600">${stats.total_received_amount?.toLocaleString() || 0}</p>
                  <p className="text-sm text-emerald-800">Won</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="search">
          <TabsList className="mb-6">
            <TabsTrigger value="search">Find Scholarships</TabsTrigger>
            <TabsTrigger value="saved">My Scholarships ({savedScholarships.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="search">
            {/* Search Filters */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Search Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Your GPA</Label>
                    <Input 
                      type="number" 
                      step="0.01" 
                      placeholder="e.g., 3.5"
                      value={searchCriteria.gpa}
                      onChange={(e) => setSearchCriteria({...searchCriteria, gpa: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Intended Major</Label>
                    <Select value={searchCriteria.major} onValueChange={(v) => setSearchCriteria({...searchCriteria, major: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select major" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="computer_science">Computer Science</SelectItem>
                        <SelectItem value="engineering">Engineering</SelectItem>
                        <SelectItem value="biology">Biology</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="arts">Arts & Humanities</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="financial"
                        checked={searchCriteria.financial_need}
                        onCheckedChange={(checked) => setSearchCriteria({...searchCriteria, financial_need: checked})}
                      />
                      <Label htmlFor="financial">Financial Need</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="firstgen"
                        checked={searchCriteria.first_gen}
                        onCheckedChange={(checked) => setSearchCriteria({...searchCriteria, first_gen: checked})}
                      />
                      <Label htmlFor="firstgen">First Gen</Label>
                    </div>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleSearch} disabled={searchLoading} className="w-full">
                      {searchLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                      Search
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scholarship List */}
            <div className="grid gap-4">
              {scholarships.map((sch) => (
                <Card key={sch.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{sch.name}</h3>
                          <Badge variant={sch.category === 'need_based' ? 'default' : 'secondary'}>
                            {categories.find(c => c.code === sch.category)?.name || sch.category}
                          </Badge>
                          {sch.match_score && (
                            <Badge variant="outline" className="text-emerald-600">
                              <TrendingUp className="h-3 w-3 mr-1" />
                              Match: {sch.match_score}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-6 text-sm text-gray-600 mb-3">
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-4 w-4" />
                            {formatAmount(sch.amount)} {sch.amount_type === 'renewable' && '(Renewable)'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Deadline: {sch.deadline}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {sch.eligibility?.slice(0, 3).map((req, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">{req}</Badge>
                          ))}
                        </div>
                        {sch.gpa_requirement && (
                          <p className="text-sm text-gray-500">Min GPA: {sch.gpa_requirement}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <Button 
                          variant={isSaved(sch.id) ? "default" : "outline"}
                          size="sm"
                          onClick={() => isSaved(sch.id) ? handleRemove(sch.id) : handleSave(sch.id)}
                        >
                          {isSaved(sch.id) ? (
                            <><BookmarkCheck className="h-4 w-4 mr-1" /> Saved</>
                          ) : (
                            <><Bookmark className="h-4 w-4 mr-1" /> Save</>
                          )}
                        </Button>
                        <a href={sch.website} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="w-full">
                            <ExternalLink className="h-4 w-4 mr-1" /> Apply
                          </Button>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="saved">
            {savedScholarships.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Bookmark className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No scholarships saved yet</p>
                  <p className="text-sm text-gray-500">Search for scholarships and save ones you're interested in</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {savedScholarships.map((sch) => (
                  <Card key={sch.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg mb-1">{sch.name}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                            <span>{formatAmount(sch.amount)}</span>
                            <span>Deadline: {sch.deadline}</span>
                          </div>
                          <Badge variant={
                            sch.status === 'received' ? 'default' :
                            sch.status === 'applied' ? 'secondary' :
                            sch.status === 'rejected' ? 'destructive' : 'outline'
                          }>
                            {sch.status}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Select 
                            value={sch.status} 
                            onValueChange={(v) => handleSave(sch.id, v)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="interested">Interested</SelectItem>
                              <SelectItem value="applied">Applied</SelectItem>
                              <SelectItem value="received">Received</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="outline" size="sm" onClick={() => handleRemove(sch.id)}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Scholarships;
